/**
 * Revisões — o lugar de onde o retorno comentado do cliente entra no sistema.
 *
 * O ciclo inteiro vive aqui: sobe-se o PDF que a AMT devolveu, o sistema lê os
 * comentários nativos, identifica de qual apreciação é o documento e abre a
 * revisão vinculada à GRD em que ele saiu. A partir daí a apreciação volta a
 * "Em revisão" e cada comentário vira item de checklist.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertCircle,
  CheckCircle2,
  ClipboardList,
  FileUp,
  Loader2,
  RefreshCw,
  Upload,
  X,
} from 'lucide-react';
import { apiService } from '@/services/api';
import { useProjeto } from '@/contexts/projeto-hook';
import { useI18n } from '@/contexts/i18n-hook';
import { LOCALE } from '@/lib/i18n';
import {
  AnalisePdfRetorno,
  IndicadoresRevisao,
  RevisaoResumo,
  STATUS_REVISAO_META,
  TIPO_ANOTACAO_ROTULO,
} from '@/types/revisao';

const BOTAO_SECUNDARIO =
  'inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark ' +
  'text-[13px] font-semibold text-rg-ink-2 dark:text-white bg-white dark:bg-schmersal-gray-darker ' +
  'hover:bg-rg-head dark:hover:bg-schmersal-blue focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 ' +
  'disabled:opacity-50 transition-colors duration-150';

const CAMPO =
  'w-full px-3 py-2 rounded-lg border border-rg-line dark:border-schmersal-gray-dark ' +
  'bg-white dark:bg-schmersal-gray-darkest text-[13px] text-rg-ink-2 dark:text-white ' +
  'focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 transition-colors duration-150';

interface GuiaResumo {
  id: string;
  numero: string;
  destinatario: string;
}

interface ApreciacaoOpcao {
  id: string;
  numero: string;
  titulo: string;
}

export function RevisoesList() {
  const { projeto } = useProjeto();
  const { t, formatarData, idioma } = useI18n();

  const [revisoes, setRevisoes] = useState<RevisaoResumo[]>([]);
  const [indicadores, setIndicadores] = useState<IndicadoresRevisao | null>(null);
  const [guias, setGuias] = useState<GuiaResumo[]>([]);
  const [apreciacoes, setApreciacoes] = useState<ApreciacaoOpcao[]>([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [filtroGrd, setFiltroGrd] = useState('');
  const [modalAberto, setModalAberto] = useState(false);

  const carregar = useCallback(async () => {
    if (!projeto) return;
    setCarregando(true);
    try {
      const [lista, kpis] = await Promise.all([
        apiService.listarRevisoes({
          projetoId: projeto.id,
          status: filtroStatus || undefined,
          guiaId: filtroGrd || undefined,
        }),
        apiService.indicadoresRevisoes(projeto.id),
      ]);
      if (lista.success && lista.data) {
        setRevisoes(lista.data);
        setErro(null);
      } else {
        setErro(lista.message ?? t('revisoes.revisoes.lista.erroCarregar'));
      }
      if (kpis.success && kpis.data) setIndicadores(kpis.data);
    } catch {
      setErro(t('revisoes.revisoes.lista.erroCarregar'));
    } finally {
      setCarregando(false);
    }
  }, [projeto, filtroStatus, filtroGrd, t]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // GRDs e apreciações abastecem os selects do modal; carregam uma vez só.
  useEffect(() => {
    if (!projeto) return;
    (async () => {
      const [g, a] = await Promise.all([
        apiService.listarGuiasRemessa(projeto.id),
        apiService.listarApreciacoes({ projetoId: projeto.id, limit: 1000 }),
      ]);
      if (g.success && g.data) setGuias(g.data.map((x) => ({ id: x.id, numero: x.numero, destinatario: x.destinatario })));
      if (a.success && Array.isArray(a.data)) {
        setApreciacoes(
          a.data.map((x: any) => ({ id: x.id, numero: x.numero, titulo: x.titulo })).sort(
            (p: ApreciacaoOpcao, q: ApreciacaoOpcao) => p.numero.localeCompare(q.numero, LOCALE[idioma]),
          ),
        );
      }
    })();
  }, [projeto, idioma]);

  if (!projeto) {
    return (
      <div className="rounded-xl border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker p-6">
        <p className="text-[13.5px] text-rg-ink-3 dark:text-schmersal-gray-light">
          {t('revisoes.revisoes.lista.semProjeto')}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-rg-ink dark:text-white">{t('revisoes.revisoes.lista.titulo')}</h1>
          <p className="mt-0.5 text-[13.5px] text-rg-ink-3 dark:text-schmersal-gray-light">
            {carregando
              ? t('comum.estado.carregando')
              : t(revisoes.length === 1 ? 'revisoes.revisoes.lista.resumoUm' : 'revisoes.revisoes.lista.resumoMuitos', {
                  total: revisoes.length,
                })}
          </p>
        </div>

        <div className="flex items-center gap-2.5">
          <button onClick={carregar} disabled={carregando} className={BOTAO_SECUNDARIO}>
            <RefreshCw className={`h-4 w-4 ${carregando ? 'animate-spin' : ''}`} />
            {t('comum.acao.atualizar')}
          </button>
          <button
            onClick={() => setModalAberto(true)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-schmersal-blue
                       dark:bg-schmersal-blue-light text-white text-[13px] font-semibold shadow-sm
                       hover:bg-schmersal-blue-dark dark:hover:bg-schmersal-blue
                       focus:outline-none focus:ring-2 focus:ring-schmersal-blue/40 transition-colors duration-150"
          >
            <FileUp className="h-4 w-4" />
            {t('revisoes.revisoes.lista.acao.registrarRetorno')}
          </button>
        </div>
      </div>

      {indicadores && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Kpi rotulo={t('revisoes.revisoes.lista.kpi.ciclosAbertos')} valor={indicadores.ciclosAbertos} destaque={indicadores.ciclosAbertos > 0} />
          <Kpi rotulo={t('revisoes.revisoes.lista.kpi.comentariosPendentes')} valor={indicadores.comentariosPendentes} destaque={indicadores.comentariosPendentes > 0} />
          <Kpi rotulo={t('revisoes.revisoes.lista.kpi.comentariosResolvidos')} valor={indicadores.comentariosResolvidos} />
          <Kpi rotulo={t('revisoes.revisoes.lista.kpi.ciclosConcluidos')} valor={indicadores.ciclosConcluidos} />
        </div>
      )}

      <div className="rounded-xl border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker
                      p-3 flex flex-wrap items-center gap-2.5">
        <select aria-label={t('revisoes.revisoes.lista.filtro.statusAria')} value={filtroStatus} onChange={(e) => setFiltroStatus(e.target.value)} className={`${CAMPO} w-auto`}>
          <option value="">{t('revisoes.revisoes.lista.filtro.statusTodos')}</option>
          <option value="aberta">{t('revisoes.revisoes.status.aberta')}</option>
          <option value="em_andamento">{t('revisoes.revisoes.status.em_andamento')}</option>
          <option value="concluida">{t('revisoes.revisoes.status.concluida')}</option>
          <option value="cancelada">{t('revisoes.revisoes.status.cancelada')}</option>
        </select>

        <select aria-label={t('revisoes.revisoes.lista.filtro.grdAria')} value={filtroGrd} onChange={(e) => setFiltroGrd(e.target.value)} className={`${CAMPO} w-auto`}>
          <option value="">{t('revisoes.revisoes.lista.filtro.grdTodas')}</option>
          {guias.map((g) => (
            <option key={g.id} value={g.id}>
              {g.numero} — {g.destinatario}
            </option>
          ))}
        </select>
      </div>

      {erro && (
        <div className="flex items-center gap-2 rounded-xl border border-red-200 dark:border-red-500/40 bg-red-50 dark:bg-red-900/20 px-4 py-3">
          <AlertCircle className="h-4 w-4 text-red-600 dark:text-red-300" />
          <span className="text-[13px] text-red-800 dark:text-red-200">{erro}</span>
        </div>
      )}

      <div className="rounded-xl border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker overflow-hidden">
        {carregando ? (
          <div className="flex items-center gap-2 px-5 py-8 text-[13px] text-rg-ink-4">
            <Loader2 className="h-4 w-4 animate-spin" />
            {t('comum.estado.carregando')}
          </div>
        ) : revisoes.length === 0 ? (
          <div className="px-5 py-10 text-center">
            <ClipboardList className="mx-auto h-8 w-8 text-rg-ink-4 mb-2" />
            <p className="text-[13.5px] font-semibold text-rg-ink-2 dark:text-white">{t('revisoes.revisoes.lista.vazio.titulo')}</p>
            <p className="mt-1 text-[13px] text-rg-ink-4">
              {t('revisoes.revisoes.lista.vazio.descricao')}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-rg-head dark:bg-schmersal-gray-darkest">
                <tr>
                  {[
                    t('revisoes.revisoes.lista.tabela.apreciacao'),
                    t('revisoes.revisoes.lista.tabela.grdOrigem'),
                    t('revisoes.revisoes.lista.tabela.revDoc'),
                    t('revisoes.revisoes.lista.tabela.recebidaEm'),
                    t('revisoes.revisoes.lista.tabela.checklist'),
                    t('comum.rotulo.status'),
                    '',
                  ].map((h, i) => (
                    <th
                      key={h || i}
                      className="px-5 py-2.5 text-left text-[11.5px] font-semibold uppercase tracking-wide text-rg-ink-3 dark:text-schmersal-gray-light"
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-rg-line dark:divide-schmersal-gray-dark">
                {revisoes.map((r) => {
                  const tratados = r.comentarios.feitos + r.comentarios.naoAplicaveis;
                  const percentual = r.comentarios.total > 0 ? Math.round((tratados / r.comentarios.total) * 100) : 0;
                  return (
                    <tr key={r.id} className="hover:bg-rg-head/60 dark:hover:bg-schmersal-gray-darkest/60 transition-colors duration-150">
                      <td className="px-5 py-3.5">
                        <Link to={`/revisoes/${r.id}`} className="text-[13px] font-semibold text-schmersal-blue dark:text-schmersal-blue-light hover:underline underline-offset-4">
                          {r.apreciacao.numero}
                        </Link>
                        <p className="text-[12.5px] text-rg-ink-3 dark:text-rg-ink-4 truncate max-w-[320px]">
                          {r.apreciacao.equipamento ?? r.apreciacao.titulo}
                        </p>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-[13px] text-rg-ink-2 dark:text-white">
                        {r.guiaOrigem?.numero ?? <span className="text-rg-ink-4">—</span>}
                      </td>
                      <td
                        className="px-5 py-3.5 text-[13px] tabular-nums text-rg-ink-2 dark:text-white"
                        title={t('revisoes.revisoes.lista.tabela.rodadaTitulo', { rodada: r.rodada })}
                      >
                        {r.numeroRevisao ?? r.rodada}
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-[13px] text-rg-ink-2 dark:text-white">
                        {formatarData(r.recebidaEm)}
                      </td>
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-2 min-w-[140px]">
                          <div className="h-1.5 flex-1 rounded-full bg-rg-line dark:bg-schmersal-gray-dark overflow-hidden">
                            <div
                              className={`h-full rounded-full transition-[width] duration-300 ${percentual === 100 ? 'bg-green-600' : 'bg-schmersal-blue'}`}
                              style={{ width: `${percentual}%` }}
                            />
                          </div>
                          <span className="text-[12px] tabular-nums text-rg-ink-3 dark:text-rg-ink-4">
                            {tratados}/{r.comentarios.total}
                          </span>
                        </div>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap">
                        <span className={`inline-flex px-2.5 py-1 rounded-md text-[12px] font-semibold ${STATUS_REVISAO_META[r.status].tom}`}>
                          {t(`revisoes.revisoes.status.${r.status}`)}
                        </span>
                      </td>
                      <td className="px-5 py-3.5 whitespace-nowrap text-right">
                        <Link to={`/revisoes/${r.id}`} className="text-[13px] font-medium text-schmersal-blue dark:text-schmersal-blue-light hover:underline underline-offset-4">
                          {t('comum.acao.abrir')}
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modalAberto && (
        <ModalRegistrarRetorno
          projetoId={projeto.id}
          guias={guias}
          apreciacoes={apreciacoes}
          onFechar={() => setModalAberto(false)}
          onRegistrada={() => {
            setModalAberto(false);
            carregar();
          }}
        />
      )}
    </div>
  );
}

function Kpi({ rotulo, valor, destaque }: { rotulo: string; valor: number; destaque?: boolean }) {
  return (
    <div className="rounded-xl border border-rg-line dark:border-schmersal-gray-dark bg-white dark:bg-schmersal-gray-darker px-4 py-3">
      <p className="text-[12px] font-medium text-rg-ink-3 dark:text-schmersal-gray-light">{rotulo}</p>
      <p className={`mt-0.5 text-2xl font-bold tabular-nums ${destaque ? 'text-amber-700 dark:text-amber-300' : 'text-rg-ink dark:text-white'}`}>
        {valor}
      </p>
    </div>
  );
}

/**
 * Duas etapas de propósito: primeiro o PDF é lido e mostrado (quantos
 * comentários, de qual apreciação, em que partes), só depois o ciclo é aberto.
 * Registrar direto significaria descobrir que o PDF era o errado depois de a
 * apreciação já ter voltado para "Em revisão".
 */
function ModalRegistrarRetorno({
  projetoId,
  guias,
  apreciacoes,
  onFechar,
  onRegistrada,
}: {
  projetoId: string;
  guias: GuiaResumo[];
  apreciacoes: ApreciacaoOpcao[];
  onFechar: () => void;
  onRegistrada: () => void;
}) {
  const { t } = useI18n();
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [analise, setAnalise] = useState<AnalisePdfRetorno | null>(null);
  const [apreciacaoId, setApreciacaoId] = useState('');
  const [guiaOrigemId, setGuiaOrigemId] = useState('');
  const [observacoes, setObservacoes] = useState('');
  const [analisando, setAnalisando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const analisar = async (arq: File) => {
    setAnalisando(true);
    setErro(null);
    setAnalise(null);
    try {
      const res = await apiService.analisarPdfRetorno({ arquivo: arq, projetoId });
      if (res.success && res.data) {
        setAnalise(res.data);
        if (res.data.apreciacao) setApreciacaoId(res.data.apreciacao.id);
        if (res.data.anotacoes.length === 0) {
          setErro(t('revisoes.revisoes.modalRetorno.erroSemComentarios'));
        }
      } else {
        setErro(res.message ?? t('revisoes.revisoes.modalRetorno.erroLerPdf'));
      }
    } catch {
      setErro(t('revisoes.revisoes.modalRetorno.erroLerPdf'));
    } finally {
      setAnalisando(false);
    }
  };

  const registrar = async () => {
    if (!apreciacaoId) {
      setErro(t('revisoes.revisoes.modalRetorno.erroApreciacaoObrigatoria'));
      return;
    }
    setSalvando(true);
    setErro(null);
    try {
      const res = await apiService.registrarRetornoCliente({
        apreciacaoId,
        arquivo,
        guiaOrigemId: guiaOrigemId || null,
        observacoes: observacoes || null,
      });
      if (res.success) onRegistrada();
      else setErro(res.message ?? t('revisoes.revisoes.modalRetorno.erroRegistrar'));
    } catch {
      setErro(t('revisoes.revisoes.modalRetorno.erroRegistrar'));
    } finally {
      setSalvando(false);
    }
  };

  const porParte = useMemo(() => {
    if (!analise) return [];
    const mapa = new Map<string, number>();
    for (const a of analise.anotacoes) {
      const chave = a.parte ?? t('revisoes.revisoes.modalRetorno.semParteIdentificada');
      mapa.set(chave, (mapa.get(chave) ?? 0) + 1);
    }
    return [...mapa.entries()];
  }, [analise, t]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white dark:bg-schmersal-gray-darker shadow-xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-rg-line dark:border-schmersal-gray-dark">
          <h2 className="text-[15px] font-bold text-rg-ink dark:text-white">{t('revisoes.revisoes.modalRetorno.titulo')}</h2>
          <button onClick={onFechar} className="p-1 rounded-md text-rg-ink-4 hover:bg-rg-head dark:hover:bg-schmersal-gray-darkest">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="block text-[12px] font-semibold text-rg-ink-2 dark:text-white mb-1">
              {t('revisoes.revisoes.modalRetorno.arquivoRotulo')}
            </label>
            <label
              className="flex flex-col items-center justify-center gap-1.5 px-4 py-6 rounded-lg border-2 border-dashed
                         border-rg-line dark:border-schmersal-gray-dark cursor-pointer
                         hover:border-schmersal-blue transition-colors duration-150"
            >
              <Upload className="h-5 w-5 text-rg-ink-4" />
              <span className="text-[13px] font-medium text-rg-ink-2 dark:text-white">
                {arquivo ? arquivo.name : t('revisoes.revisoes.modalRetorno.arquivoEscolher')}
              </span>
              <span className="text-[12px] text-rg-ink-4">{t('revisoes.revisoes.modalRetorno.arquivoDica')}</span>
              <input
                type="file"
                accept="application/pdf,.pdf"
                className="hidden"
                onChange={(e) => {
                  const arq = e.target.files?.[0] ?? null;
                  setArquivo(arq);
                  if (arq) analisar(arq);
                }}
              />
            </label>
          </div>

          {analisando && (
            <div className="flex items-center gap-2 text-[13px] text-rg-ink-3 dark:text-rg-ink-4">
              <Loader2 className="h-4 w-4 animate-spin" />
              {t('revisoes.revisoes.modalRetorno.lendoPdf')}
            </div>
          )}

          {analise && (
            <div className="rounded-lg border border-rg-line dark:border-schmersal-gray-dark bg-rg-head/50 dark:bg-schmersal-gray-darkest p-3.5 space-y-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
                <span className="text-[13px] font-semibold text-rg-ink-1 dark:text-white">
                  {t(
                    analise.anotacoes.length === 1
                      ? 'revisoes.revisoes.modalRetorno.resumoUm'
                      : 'revisoes.revisoes.modalRetorno.resumoMuitos',
                    { comentarios: analise.anotacoes.length, paginas: analise.totalPaginas },
                  )}
                </span>
              </div>
              {analise.apreciacao && (
                <p className="text-[12.5px] text-rg-ink-3 dark:text-rg-ink-4">
                  {t('revisoes.revisoes.modalRetorno.documentoIdentificado')}{' '}
                  <b className="font-semibold text-rg-ink-2 dark:text-white">{analise.apreciacao.numero}</b> — {analise.apreciacao.titulo}
                </p>
              )}
              {porParte.length > 0 && (
                <ul className="text-[12.5px] text-rg-ink-3 dark:text-rg-ink-4 space-y-0.5">
                  {porParte.map(([parte, qtd]) => (
                    <li key={parte}>
                      {t(
                        qtd === 1
                          ? 'revisoes.revisoes.modalRetorno.porParteUm'
                          : 'revisoes.revisoes.modalRetorno.porParteMuitos',
                        { parte, qtd },
                      )}
                    </li>
                  ))}
                </ul>
              )}
              {analise.anotacoes.length > 0 && (
                <p className="text-[12px] text-rg-ink-4 italic">
                  {t('revisoes.revisoes.modalRetorno.primeiroComentario', {
                    texto: analise.anotacoes[0].texto.slice(0, 120),
                    tipo: TIPO_ANOTACAO_ROTULO[analise.anotacoes[0].tipo]
                      ? t(`revisoes.revisoes.tipoAnotacao.${analise.anotacoes[0].tipo}`)
                      : analise.anotacoes[0].tipo,
                    pagina: analise.anotacoes[0].pagina,
                  })}
                </p>
              )}
            </div>
          )}

          <div>
            <label className="block text-[12px] font-semibold text-rg-ink-2 dark:text-white mb-1">{t('revisoes.revisoes.modalRetorno.apreciacaoRotulo')}</label>
            <select value={apreciacaoId} onChange={(e) => setApreciacaoId(e.target.value)} className={CAMPO}>
              <option value="">{t('revisoes.revisoes.modalRetorno.apreciacaoPlaceholder')}</option>
              {apreciacoes.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.numero} — {a.titulo}
                </option>
              ))}
            </select>
            <p className="mt-1 text-[11.5px] text-rg-ink-4">
              {t('revisoes.revisoes.modalRetorno.apreciacaoDica')}
            </p>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-rg-ink-2 dark:text-white mb-1">{t('revisoes.revisoes.modalRetorno.grdRotulo')}</label>
            <select value={guiaOrigemId} onChange={(e) => setGuiaOrigemId(e.target.value)} className={CAMPO}>
              <option value="">{t('revisoes.revisoes.modalRetorno.grdVazia')}</option>
              {guias.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.numero} — {g.destinatario}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-[12px] font-semibold text-rg-ink-2 dark:text-white mb-1">{t('comum.rotulo.observacoes')}</label>
            <textarea
              value={observacoes}
              onChange={(e) => setObservacoes(e.target.value)}
              rows={2}
              placeholder={t('revisoes.revisoes.modalRetorno.observacoesPlaceholder')}
              className={CAMPO}
            />
          </div>

          {erro && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 dark:border-amber-500/40 bg-amber-50 dark:bg-amber-900/20 px-3 py-2">
              <AlertCircle className="h-4 w-4 mt-0.5 text-amber-700 dark:text-amber-300 shrink-0" />
              <span className="text-[12.5px] text-amber-900 dark:text-amber-100">{erro}</span>
            </div>
          )}

          <p
            className="text-[12px] text-rg-ink-4"
            dangerouslySetInnerHTML={{ __html: t('revisoes.revisoes.modalRetorno.aviso') }}
          />
        </div>

        <div className="flex justify-end gap-2.5 px-5 py-4 border-t border-rg-line dark:border-schmersal-gray-dark">
          <button onClick={onFechar} className={BOTAO_SECUNDARIO}>
            {t('comum.acao.cancelar')}
          </button>
          <button
            onClick={registrar}
            disabled={salvando || analisando || !apreciacaoId || (!!analise && analise.anotacoes.length === 0)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-schmersal-blue
                       dark:bg-schmersal-blue-light text-white text-[13px] font-semibold shadow-sm
                       hover:bg-schmersal-blue-dark disabled:opacity-50 transition-colors duration-150"
          >
            {salvando && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('revisoes.revisoes.modalRetorno.acaoAbrir')}
          </button>
        </div>
      </div>
    </div>
  );
}
